import { useState, useEffect } from "react";
import axios from "axios";
import { CreditCard, Plus, Pencil, Trash2, Loader2, X, Package, Tag } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (minor, sym) => `${sym}${(Number(minor || 0) / 100).toLocaleString()}`;

const emptyPlan = {
  name: "", price_inr: 0, price_usd: 0, opt_credits: 200, scan_credits: 200, scans_limit: 200,
  period: "monthly", duration_days: 45, bonus_scans: 0, description: "", active: true,
  offer: { active: false, price_inr: 0, price_usd: 0, label: "", expires_at: "" },
};
const emptyPack = { name: "", credit_type: "scan", credits: 50, scans: 50, price_inr: 0, price_usd: 0, active: true };

const PERIODS = [
  { v: "weekly", label: "Weekly (7 days)" },
  { v: "monthly", label: "Monthly (30 days)" },
  { v: "quarterly", label: "Quarterly (90 days)" },
  { v: "annual", label: "Annual (365 days)" },
  { v: "custom", label: "Custom (set days)" },
];

export default function ManageSubscriptions() {
  const [catalog, setCatalog] = useState({ plans: {}, scan_packs: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editor, setEditor] = useState(null); // { kind, id|null, form }
  const [fx, setFx] = useState(null); // live USD→INR rate

  const load = () => {
    setLoading(true);
    axios.get(`${API}/admin/subscription-catalog`)
      .then((r) => setCatalog(r.data))
      .catch(() => toast.error("Failed to load subscriptions"))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    axios.get(`${API}/admin/fx-rate`).then((r) => setFx(r.data?.usd_inr || null)).catch(() => {});
  }, []);

  const toMinor = (v) => Math.round(parseFloat(v || 0) * 100);
  const fromMinor = (v) => (Number(v || 0) / 100);

  const openCreate = (kind) => {
    const base = kind === "plan" ? emptyPlan : emptyPack;
    setEditor({ kind, id: null, form: JSON.parse(JSON.stringify(base)), major: true });
  };
  const openEdit = (kind, item) => {
    const f = JSON.parse(JSON.stringify(item));
    // convert minor → major for editing
    f.price_inr = fromMinor(f.price_inr); f.price_usd = fromMinor(f.price_usd);
    if (kind === "plan") {
      f.offer = f.offer || { active: false, price_inr: 0, price_usd: 0, label: "", expires_at: "" };
      f.offer.price_inr = fromMinor(f.offer.price_inr); f.offer.price_usd = fromMinor(f.offer.price_usd);
      if (f.opt_credits == null) f.opt_credits = f.scans_limit || 0;
      if (f.scan_credits == null) f.scan_credits = f.scans_limit || 0;
    } else {
      if (f.credit_type == null) f.credit_type = "scan";
      if (f.credits == null) f.credits = f.scans || 0;
    }
    setEditor({ kind, id: item.id, form: f, major: true });
  };

  const save = async () => {
    const { kind, id, form } = editor;
    const payload = { kind, ...form };
    payload.price_inr = toMinor(form.price_inr);
    payload.price_usd = toMinor(form.price_usd);
    if (kind === "plan") {
      // Keep the legacy scans_limit in sync for unlimited-cap detection + old readers.
      const optC = parseInt(form.opt_credits || 0);
      const scanC = parseInt(form.scan_credits || 0);
      payload.opt_credits = optC;
      payload.scan_credits = scanC;
      payload.scans_limit = Math.max(optC, scanC);
    }
    if (kind === "pack") {
      const credits = parseInt(form.credits || 0);
      payload.credits = credits;
      payload.scans = credits; // legacy mirror
    }
    if (kind === "plan" && form.offer) {
      payload.offer = {
        ...form.offer,
        price_inr: toMinor(form.offer.price_inr),
        price_usd: toMinor(form.offer.price_usd),
      };
    }
    if (!payload.name?.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (id) await axios.put(`${API}/admin/subscription-catalog/${kind}/${id}`, payload);
      else await axios.post(`${API}/admin/subscription-catalog`, payload);
      toast.success(`Saved ${form.name}`);
      setEditor(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (kind, item) => {
    if (!window.confirm(`Delete "${item.name}"? Plans in use will be archived (hidden from new buyers).`)) return;
    try {
      const r = await axios.delete(`${API}/admin/subscription-catalog/${kind}/${item.id}`);
      toast.success(r.data?.archived ? `${item.name} archived (in use)` : `${item.name} deleted`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const setF = (path, val) => {
    setEditor((ed) => {
      const f = { ...ed.form };
      if (path.startsWith("offer.")) { f.offer = { ...f.offer, [path.slice(6)]: val }; }
      else f[path] = val;
      return { ...ed, form: f };
    });
  };

  // ── Auto currency conversion: edit either currency, the other fills from the live rate.
  //    The converted value is only a starting point — admin can still overwrite it.
  //    Saved prices are FIXED (no live re-pricing after save).
  const setPrice = (prefix, cur, val) => {
    const inrKey = prefix ? `${prefix}.price_inr` : "price_inr";
    const usdKey = prefix ? `${prefix}.price_usd` : "price_usd";
    setF(cur === "inr" ? inrKey : usdKey, val);
    const num = parseFloat(val);
    if (!fx || isNaN(num)) return;
    if (cur === "inr") setF(usdKey, num > 0 ? Math.round(num / fx) : 0); // paise → cents
    else setF(inrKey, num > 0 ? Math.round(num * fx) : 0); // cents → paise
  };

  if (loading) {
    return <div className="analytics-loading" data-testid="manage-subs-loading"><div className="processing-spinner" /></div>;
  }

  const plans = Object.values(catalog.plans || {});
  const packs = Object.values(catalog.scan_packs || {});

  return (
    <div className="page-container fade-in" data-testid="manage-subscriptions-page">
      <div className="page-header">
        <div>
          <h1 style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}><CreditCard size={22} /> Manage Subscriptions</h1>
          <p style={{ color: "var(--text-secondary)" }}>Create, edit, price and retire plans & scan packs. Changes go live on the customer pricing page and checkout instantly.</p>
        </div>
      </div>

      {/* Plans */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "0.5rem 0 0.8rem" }}>
        <h2 style={{ fontSize: "1.05rem", display: "flex", gap: 8, alignItems: "center" }}><Tag size={18} /> Subscription Plans</h2>
        <button className="btn-primary" data-testid="add-plan-btn" onClick={() => openCreate("plan")}
          style={{ display: "flex", gap: 6, alignItems: "center", padding: "0.5rem 0.9rem" }}><Plus size={16} /> Add Plan</button>
      </div>
      <div className="card" style={{ padding: "0.4rem 1.2rem" }}>
        {plans.length === 0 && <div style={{ padding: "1rem", color: "var(--text-secondary)" }}>No plans yet.</div>}
        {plans.map((p) => (
          <div key={p.id} data-testid={`plan-row-${p.id}`}
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", padding: "0.9rem 0", borderBottom: "1px solid var(--border-main)", opacity: p.archived ? 0.55 : 1 }}>
            <div>
              <div style={{ fontWeight: 600, display: "flex", gap: 8, alignItems: "center" }}>
                {p.name}
                {p.archived && <span style={badge("#9ca3af")}>ARCHIVED</span>}
                {!p.active && !p.archived && <span style={badge("#f59e0b")}>INACTIVE</span>}
                {p.offer?.active && <span style={badge("#10b981")}>OFFER</span>}
              </div>
              <div style={{ fontSize: "0.82rem", color: "var(--text-secondary)" }}>
                {money(p.price_inr, "₹")} / {money(p.price_usd, "$")} · {(p.opt_credits ?? p.scans_limit) >= 999999 ? "Unlimited" : (p.opt_credits ?? p.scans_limit)} opt · {(p.scan_credits ?? p.scans_limit) >= 999999 ? "Unlimited" : (p.scan_credits ?? p.scans_limit)} scan · {p.period}
                {p.bonus_scans > 0 ? ` · +${p.bonus_scans} bonus` : ""}
              </div>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button className="btn-secondary" data-testid={`edit-plan-${p.id}`} onClick={() => openEdit("plan", p)} style={iconBtn}><Pencil size={15} /></button>
              <button className="btn-secondary" data-testid={`delete-plan-${p.id}`} onClick={() => remove("plan", p)} style={{ ...iconBtn, color: "#ef4444" }}><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>

      {/* Scan packs */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "1.5rem 0 0.8rem" }}>
        <h2 style={{ fontSize: "1.05rem", display: "flex", gap: 8, alignItems: "center" }}><Package size={18} /> Scan Packs (one-time add-ons)</h2>
        <button className="btn-primary" data-testid="add-pack-btn" onClick={() => openCreate("pack")}
          style={{ display: "flex", gap: 6, alignItems: "center", padding: "0.5rem 0.9rem" }}><Plus size={16} /> Add Pack</button>
      </div>
      <div className="card" style={{ padding: "0.4rem 1.2rem" }}>
        {packs.length === 0 && <div style={{ padding: "1rem", color: "var(--text-secondary)" }}>No scan packs yet.</div>}
        {packs.map((p) => (
          <div key={p.id} data-testid={`pack-row-${p.id}`}
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", padding: "0.9rem 0", borderBottom: "1px solid var(--border-main)", opacity: p.archived ? 0.55 : 1 }}>
            <div>
              <div style={{ fontWeight: 600 }}>{p.name} {!p.active && <span style={badge("#f59e0b")}>INACTIVE</span>}</div>
              <div style={{ fontSize: "0.82rem", color: "var(--text-secondary)" }}>{money(p.price_inr, "₹")} / {money(p.price_usd, "$")} · {p.credits ?? p.scans} {(p.credit_type || "scan") === "optimization" ? "optimizations" : "scans"}</div>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button className="btn-secondary" data-testid={`edit-pack-${p.id}`} onClick={() => openEdit("pack", p)} style={iconBtn}><Pencil size={15} /></button>
              <button className="btn-secondary" data-testid={`delete-pack-${p.id}`} onClick={() => remove("pack", p)} style={{ ...iconBtn, color: "#ef4444" }}><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>

      {editor && (
        <div style={overlay} data-testid="subs-editor-modal" onClick={(e) => { if (e.target === e.currentTarget) setEditor(null); }}>
          <div className="card" style={modal}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
              <h3 style={{ margin: 0 }}>{editor.id ? "Edit" : "New"} {editor.kind === "plan" ? "Plan" : "Scan Pack"}</h3>
              <button onClick={() => setEditor(null)} style={iconBtn} data-testid="close-editor"><X size={18} /></button>
            </div>
            <div style={{ display: "grid", gap: "0.8rem" }}>
              <Field label="Name"><input data-testid="f-name" style={inp} value={editor.form.name} onChange={(e) => setF("name", e.target.value)} /></Field>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.8rem" }}>
                <Field label={`Price ₹ paise${Number(editor.form.price_inr) > 0 ? ` (= ₹${(Number(editor.form.price_inr) / 100).toLocaleString("en-IN", { maximumFractionDigits: 2 })})` : " (49900 = ₹499)"}`}>
                  <input data-testid="f-inr" type="number" style={inp} value={editor.form.price_inr} onChange={(e) => setPrice("", "inr", e.target.value)} />
                </Field>
                <Field label={`Price $ cents${Number(editor.form.price_usd) > 0 ? ` (= $${(Number(editor.form.price_usd) / 100).toFixed(2)})` : " (599 = $5.99)"}`}>
                  <input data-testid="f-usd" type="number" style={inp} value={editor.form.price_usd} onChange={(e) => setPrice("", "usd", e.target.value)} />
                </Field>
              </div>
              <div style={hint} data-testid="fx-hint">
                {fx
                  ? <>Live rate: $1 ≈ ₹{fx} (ECB) — typing one currency auto-fills the other; edit either to override. Prices are locked in once saved.</>
                  : <>Live exchange rate unavailable — set both prices manually.</>}
              </div>
              {editor.kind === "plan" ? (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.8rem" }}>
                    <Field label="Optimizations per cycle (999999 = unlimited)"><input data-testid="f-opt-credits" type="number" style={inp} value={editor.form.opt_credits} onChange={(e) => setF("opt_credits", parseInt(e.target.value || 0))} /></Field>
                    <Field label="Scans per cycle (999999 = unlimited)"><input data-testid="f-scan-credits" type="number" style={inp} value={editor.form.scan_credits} onChange={(e) => setF("scan_credits", parseInt(e.target.value || 0))} /></Field>
                  </div>
                  <Field label="Billing period">
                    <select data-testid="f-period" style={inp} value={editor.form.period} onChange={(e) => setF("period", e.target.value)}>
                      {PERIODS.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
                    </select>
                  </Field>
                  {editor.form.period === "custom" && (
                    <Field label="Cycle length (days)">
                      <input data-testid="f-duration" type="number" min="1" style={inp} value={editor.form.duration_days || ""} placeholder="e.g. 45" onChange={(e) => setF("duration_days", parseInt(e.target.value || 0))} />
                    </Field>
                  )}
                  {editor.form.period === "annual" && Number(editor.form.price_inr) > 0 && (
                    <div style={hint} data-testid="annual-hint">
                      ≈ ₹{Math.round(Number(editor.form.price_inr) / 1200).toLocaleString()}/mo effective · Tip: annual plans typically price at ~10× the monthly price (2 months free) to convert best.
                    </div>
                  )}
                  {["weekly", "quarterly", "custom"].includes(editor.form.period) && Number(editor.form.price_inr) > 0 && (
                    <div style={hint}>
                      Subscribers get {editor.form.opt_credits >= 999999 ? "unlimited" : editor.form.opt_credits} optimizations + {editor.form.scan_credits >= 999999 ? "unlimited" : editor.form.scan_credits} scans for {editor.form.period === "weekly" ? "7" : editor.form.period === "quarterly" ? "90" : editor.form.duration_days || "?"} days per purchase.
                    </div>
                  )}
                  <Field label="Bonus scans (one-time, granted on purchase)"><input data-testid="f-bonus" type="number" style={inp} value={editor.form.bonus_scans} onChange={(e) => setF("bonus_scans", parseInt(e.target.value || 0))} /></Field>
                  <Field label="Description"><input data-testid="f-desc" style={inp} value={editor.form.description} onChange={(e) => setF("description", e.target.value)} /></Field>
                  <label style={chk}><input type="checkbox" data-testid="f-active" checked={editor.form.active} onChange={(e) => setF("active", e.target.checked)} /> Active (visible to customers)</label>

                  <div style={{ border: "1px solid var(--border-main)", borderRadius: 10, padding: "0.8rem" }}>
                    <label style={{ ...chk, fontWeight: 600 }}><input type="checkbox" data-testid="f-offer-active" checked={editor.form.offer?.active} onChange={(e) => setF("offer.active", e.target.checked)} /> Time-limited offer</label>
                    {editor.form.offer?.active && (
                      <div style={{ display: "grid", gap: "0.7rem", marginTop: "0.7rem" }}>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.8rem" }}>
                          <Field label="Offer ₹ INR"><input data-testid="f-offer-inr" type="number" style={inp} value={editor.form.offer.price_inr} onChange={(e) => setPrice("offer", "inr", e.target.value)} /></Field>
                          <Field label="Offer $ USD"><input data-testid="f-offer-usd" type="number" style={inp} value={editor.form.offer.price_usd} onChange={(e) => setPrice("offer", "usd", e.target.value)} /></Field>
                        </div>
                        <Field label="Offer label (e.g. Launch Sale)"><input data-testid="f-offer-label" style={inp} value={editor.form.offer.label} onChange={(e) => setF("offer.label", e.target.value)} /></Field>
                        <Field label="Expires at (blank = no expiry)"><input data-testid="f-offer-expiry" type="datetime-local" style={inp} value={editor.form.offer.expires_at || ""} onChange={(e) => setF("offer.expires_at", e.target.value)} /></Field>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <Field label="Credit type (which wallet this pack tops up)">
                    <select data-testid="f-pack-credit-type" style={inp} value={editor.form.credit_type} onChange={(e) => setF("credit_type", e.target.value)}>
                      <option value="scan">Scan credits</option>
                      <option value="optimization">Optimization credits</option>
                    </select>
                  </Field>
                  <Field label={`${(editor.form.credit_type || "scan") === "optimization" ? "Optimizations" : "Scans"} in this pack`}><input data-testid="f-pack-credits" type="number" style={inp} value={editor.form.credits} onChange={(e) => setF("credits", parseInt(e.target.value || 0))} /></Field>
                  <label style={chk}><input type="checkbox" data-testid="f-active" checked={editor.form.active} onChange={(e) => setF("active", e.target.checked)} /> Active (visible to customers)</label>
                </>
              )}
              <button className="btn-primary" data-testid="save-subs-btn" onClick={save} disabled={saving} style={{ marginTop: "0.4rem" }}>
                {saving ? <Loader2 size={16} className="spin" /> : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const badge = (c) => ({ fontSize: "0.62rem", padding: "1px 7px", borderRadius: 999, background: `${c}22`, color: c });
const iconBtn = { padding: "0.4rem 0.55rem", display: "inline-flex", alignItems: "center" };
const inp = { width: "100%", padding: "0.5rem 0.65rem", borderRadius: 8, border: "1px solid var(--border-main)", background: "var(--bg-main)", color: "var(--text-main)" };
const chk = { display: "flex", gap: 8, alignItems: "center", fontSize: "0.88rem" };
const hint = { fontSize: "0.74rem", color: "var(--text-secondary)", background: "var(--bg-main)", border: "1px dashed var(--border-main)", borderRadius: 8, padding: "0.5rem 0.7rem", lineHeight: 1.5 };
const overlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "1rem" };
const modal = { width: "100%", maxWidth: 520, maxHeight: "90vh", overflowY: "auto", padding: "1.5rem" };

function Field({ label, children }) {
  return (
    <div>
      <label style={{ fontSize: "0.78rem", color: "var(--text-secondary)", display: "block", marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  );
}
